import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, FileText, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge, Button, cn, Dialog, Field, Input, Select, Sheet, Spinner, Textarea } from '../components/ui.tsx'
import { get, post, put } from '../lib/api.ts'
import { fmtMoney } from '../lib/format.ts'
import { LOCALE, t } from '../lib/i18n.ts'

type Kind = 'despesa' | 'receita'
type Status = 'ok' | 'atencao' | 'estourado' | 'sem_previsao'
interface Line { kind: Kind; category: string; label: string; budget: number; actual: number; expected: number; projected: number; pct: number | null; status: Status; weights: number[] | null }
interface Totals { budget: number; actual: number; expected: number; projected: number; pct: number | null }
interface BudgetView {
  year: number; exists: boolean; note: string | null; approvedAssemblyId: string | null; approvedAssemblyTitle: string | null; documentId: string | null
  comparison: { year: number; monthsElapsed: number; lines: Line[]; totals: Record<Kind, Totals>; balance: { budget: number; actual: number; projected: number } }
}
interface Draft { amounts: Record<string, string>; weights: Record<string, number[] | null>; note: string; approved: string }

const MONTHS_SHORT = Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(LOCALE, { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, i, 15))).replace('.', ''))
/** Shares in % from any non-negative twelve numbers (the server normalises; the UI only describes). */
const shares = (w: number[]): number[] => { const sum = w.reduce((a, b) => a + b, 0) || 1; return w.map((x) => Math.round((100 * x) / sum)) }
const describeWeights = (w: number[]) => shares(w).map((pct, i) => `${MONTHS_SHORT[i]} ${pct}%`).join(', ')
/** The button text in edit mode: the months that stand out, or "uniforme". */
const seasonLabel = (w: number[] | null): string => {
  if (!w) return t('Sazonalidade: uniforme')
  const top = shares(w).map((pct, i) => ({ pct, i })).filter((x) => x.pct >= 12).sort((a, b) => b.pct - a.pct).slice(0, 3)
  return t('Sazonal · {months}', { months: top.length ? top.map((x) => `${MONTHS_SHORT[x.i]} ${x.pct}%`).join(', ') : t('ajustada') })
}

export const BUDGET_CATS: [string, string][] = [['agua', t('Água')], ['energia', t('Energia')], ['gas', t('Gás')], ['pessoal', t('Pessoal')], ['manutencao', t('Manutenção')], ['limpeza', t('Limpeza')], ['seguranca', t('Segurança')], ['elevador', t('Elevador')], ['administradora', t('Administradora')], ['seguro', t('Seguro')], ['obras', t('Obras')], ['impostos', t('Impostos')], ['bancarias', t('Tarifas bancárias')], ['taxa_condominial', t('Taxa condominial')], ['fundo_reserva', t('Fundo de reserva')], ['multas_juros', t('Multas e juros')], ['reservas_areas', t('Reservas de áreas')], ['outros', t('Outros')]]
const KIND_LABEL: Record<Kind, string> = { despesa: t('Despesas'), receita: t('Receitas') }
const STATUS_LABEL: Record<Status, string> = { ok: t('no plano'), atencao: t('atenção'), estourado: t('acima do previsto'), sem_previsao: t('sem previsão') }
const STATUS_TONE: Record<Status, 'alerta' | 'carimbo' | 'margem' | 'neutral'> = { ok: 'carimbo', atencao: 'alerta', estourado: 'margem', sem_previsao: 'neutral' }
const label = (c: string) => BUDGET_CATS.find((x) => x[0] === c)?.[1] ?? c
/** `1.234,50` and `1234,5` (comma decimal, dots as thousands) or `1234.5` (no comma: dot decimal). */
export const toNumber = (v: string): number => {
  const s = String(v).trim()
  if (!s) return 0
  return Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s) || 0
}

/** Previsão orçamentária (ADR 0021): the yearly plan the assembly approves, next to what was booked so far. */
export function Budget({ condoId, slug, manage }: { condoId: string; slug: string; manage: boolean }) {
  const qc = useQueryClient()
  const [year, setYear] = useState(new Date().getFullYear())
  const q = useQuery({ queryKey: ['budget', condoId, year], queryFn: () => get<BudgetView>(`/api/condos/${condoId}/finance/budgets/${year}`), enabled: Boolean(condoId) })
  const asms = useQuery({ queryKey: ['assemblies', condoId], queryFn: () => get<{ assemblies: { id: string; title: string; status: string }[] }>(`/api/condos/${condoId}/assemblies`), enabled: Boolean(condoId && manage) })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [adding, setAdding] = useState<Record<Kind, string>>({ despesa: '', receita: '' })
  const [season, setSeason] = useState<{ key: string; label: string; weights: number[] | null } | null>(null)
  const d = q.data
  const lines = d?.comparison.lines ?? []
  const url = (rest = '') => `/api/condos/${condoId}/finance/budgets/${year}${rest}`
  const invalidate = () => qc.invalidateQueries({ queryKey: ['budget', condoId] })
  const startEdit = () => setDraft({ amounts: Object.fromEntries(lines.filter((l) => l.budget > 0).map((l) => [`${l.kind}:${l.category}`, String(l.budget).replace('.', ',')])), weights: Object.fromEntries(lines.filter((l) => l.budget > 0).map((l) => [`${l.kind}:${l.category}`, l.weights])), note: d?.note ?? '', approved: d?.approvedAssemblyId ?? '' })
  const save = useMutation({
    mutationFn: () => {
      const items = Object.entries(draft!.amounts).map(([k, v]) => { const [kind, category] = k.split(':') as [Kind, string]; return { kind, category, amount: toNumber(v), weights: draft!.weights[k] ?? null } }).filter((l) => l.amount > 0)
      return put(url(), { lines: items, note: draft!.note.trim() || null, approvedAssemblyId: draft!.approved || null })
    },
    onSuccess: () => { toast.success(t('Previsão salva')); setDraft(null); invalidate() },
    onError: (e) => toast.error((e as Error).message),
  })
  const suggest = useMutation({
    mutationFn: () => get<{ basis: 'ano_anterior' | 'ano_atual' | 'vazio'; lines: { kind: Kind; category: string; amount: number; weights?: number[] | null }[] }>(url('/suggestion')),
    onSuccess: (r) => {
      if (r.lines.length === 0) { toast.error(t('Sem lançamentos para sugerir. Registre despesas e receitas em Lançamentos primeiro.')); return }
      setDraft((e) => ({ amounts: Object.fromEntries(r.lines.map((l) => [`${l.kind}:${l.category}`, String(l.amount).replace('.', ',')])), weights: Object.fromEntries(r.lines.map((l) => [`${l.kind}:${l.category}`, l.weights ?? null])), note: e?.note ?? d?.note ?? '', approved: e?.approved ?? d?.approvedAssemblyId ?? '' }))
      toast.success(r.basis === 'ano_anterior' ? t('Preenchido com o realizado do ano anterior. Ajuste e salve.') : t('Preenchido com o realizado deste ano, anualizado. Ajuste e salve.'))
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const pdf = useMutation({ mutationFn: () => post<{ documentId: string }>(url('/pdf'), { visibility: 'conselho' }), onSuccess: () => { toast.success(t('PDF gerado no acervo, visível ao conselho')); invalidate() }, onError: (e) => toast.error((e as Error).message) })

  const rowsFor = (kind: Kind): { category: string; line: Line | undefined }[] => {
    const cats = new Set<string>(lines.filter((l) => l.kind === kind).map((l) => l.category))
    if (draft) for (const k of Object.keys(draft.amounts)) if (k.startsWith(`${kind}:`)) cats.add(k.slice(kind.length + 1))
    return BUDGET_CATS.filter(([c]) => cats.has(c)).map(([c]) => ({ category: c, line: lines.find((l) => l.kind === kind && l.category === c) }))
  }
  const months = d?.comparison.monthsElapsed ?? 0
  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="quiet" aria-label={t('Ano anterior')} onClick={() => { setDraft(null); setYear(year - 1) }}><ChevronLeft className="size-5" /></Button>
          <h2 className="text-lg font-bold">{t('Previsão')}{' '}{year}</h2>
          <Button size="sm" variant="quiet" aria-label={t('Próximo ano')} onClick={() => { setDraft(null); setYear(year + 1) }}><ChevronRight className="size-5" /></Button>
        </div>
        {manage && !draft && d && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={startEdit}>{d.exists ? t('Editar previsão') : t('Montar previsão')}</Button>
            {d.exists && <Button variant="outline" busy={pdf.isPending} onClick={() => pdf.mutate()}><FileText className="size-4" />{' '}{t('Gerar PDF para a assembleia')}</Button>}
          </div>
        )}
        {manage && draft && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="quiet" busy={suggest.isPending} onClick={() => suggest.mutate()}><Sparkles className="size-4" />{' '}{t('Sugerir pelo realizado')}</Button>
            <Button variant="quiet" onClick={() => setDraft(null)}>{t('Cancelar')}</Button>
            <Button busy={save.isPending} onClick={() => save.mutate()}>{t('Salvar previsão')}</Button>
          </div>
        )}
      </div>
      <p className="max-w-prose text-sm text-tinta-2">
        {t('A previsão é a que a assembleia aprova (Código Civil, art. 1.350); o realizado vem dos lançamentos.')}{' '}{months > 0 && months < 12 ? t('Realizado até o mês {n} de 12.', { n: months }) : months === 12 ? t('Ano encerrado.') : t('Ano ainda não iniciado.')}
      </p>
      {!d ? <Spinner /> : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {(['despesa', 'receita'] as Kind[]).map((k) => {
              const tot = d.comparison.totals[k]
              return (
                <div key={k} className="rounded-ficha border border-pauta bg-folha p-3">
                  <p className="text-sm text-tinta-2">{KIND_LABEL[k]}</p>
                  <p className="text-lg font-bold">{fmtMoney(tot.actual)} <span className="text-sm font-normal text-tinta-2">{t('de')}{' '}{fmtMoney(tot.budget)}{tot.pct !== null ? ` (${tot.pct}%)` : ''}</span></p>
                </div>
              )
            })}
            <div className="rounded-ficha border border-pauta bg-folha p-3">
              <p className="text-sm text-tinta-2">{t('Saldo')}</p>
              <p className="text-lg font-bold">{fmtMoney(d.comparison.balance.actual)} <span className="text-sm font-normal text-tinta-2">{t('previsto')}{' '}{fmtMoney(d.comparison.balance.budget)}</span></p>
              {months > 0 && months < 12 && <p className="text-sm text-tinta-2">{t('Projeção do ano:')}{' '}{fmtMoney(d.comparison.balance.projected)}</p>}
            </div>
          </div>
          {(['despesa', 'receita'] as Kind[]).map((kind) => {
            const rows = rowsFor(kind)
            return (
              <div key={kind}>
                <h3 className="mb-2 font-bold">{KIND_LABEL[kind]}</h3>
                {rows.length === 0 && !draft && <p className="text-sm text-tinta-2">{t('Nenhuma categoria prevista ou lançada.')}</p>}
                {rows.length > 0 && (
                  <Sheet>
                    {rows.map(({ category, line }) => {
                      const key = `${kind}:${category}`
                      const budget = draft ? toNumber(draft.amounts[key] ?? '') : (line?.budget ?? 0)
                      const actual = line?.actual ?? 0
                      const pct = budget > 0 ? Math.min(100, Math.round((actual / budget) * 100)) : 0
                      return (
                        <div key={key} className="grid gap-2 border-b border-pauta px-4 py-3 last:border-b-0 sm:grid-cols-[1fr_auto] sm:items-center">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{label(category)}</span>
                              {line && !draft && <Badge tone={STATUS_TONE[line.status]}>{STATUS_LABEL[line.status]}</Badge>}
                              {line?.weights && !draft && <span title={describeWeights(line.weights)}><Badge tone="neutral">{t('sazonal')}</Badge></span>}
                            </div>
                            <p className="text-sm text-tinta-2">{t('Realizado')}{' '}{fmtMoney(actual)}{line && line.budget > 0 ? t(' de {budget} ({pct}%)', { budget: fmtMoney(line.budget), pct: line.pct }) : ''}{line && months > 0 && months < 12 && line.budget > 0 ? t(', esperado até agora {amount}', { amount: fmtMoney(line.expected) }) : ''}</p>
                            {budget > 0 && <div className="mt-1 h-1.5 w-full max-w-xs rounded-full bg-pauta"><div className={cn('h-1.5 rounded-full', line?.status === 'estourado' ? 'bg-margem' : line?.status === 'atencao' ? 'bg-alerta' : 'bg-caneta')} style={{ width: `${pct}%` }} /></div>}
                          </div>
                          {draft ? (
                            <>
                            <Field label={t('Previsto (R$)')} htmlFor={`bd-${kind}-${category}`}>
                              <Input id={`bd-${kind}-${category}`} inputMode="decimal" className="sm:w-40" value={draft.amounts[key] ?? ''} onChange={(e) => setDraft({ ...draft, amounts: { ...draft.amounts, [key]: e.target.value } })} placeholder="0,00" />
                            </Field>
                            <button type="button" data-testid={`season-${kind}-${category}`} className="text-left text-sm font-semibold text-caneta underline sm:col-span-2" onClick={() => setSeason({ key, label: label(category), weights: draft.weights[key] ?? null })}>{seasonLabel(draft.weights[key] ?? null)}</button>
                            </>
                          ) : (
                            <span className="font-semibold sm:text-right">{fmtMoney(budget)}</span>
                          )}
                        </div>
                      )
                    })}
                  </Sheet>
                )}
                {draft && (
                  <div className="mt-2 flex items-end gap-2">
                    <Field label={t('Adicionar categoria em {kind}', { kind: KIND_LABEL[kind].toLowerCase() })} htmlFor={`bd-add-${kind}`}>
                      <Select id={`bd-add-${kind}`} value={adding[kind]} onChange={(e) => {
                        const c = e.target.value
                        if (!c) return
                        setDraft({ ...draft, amounts: { ...draft.amounts, [`${kind}:${c}`]: draft.amounts[`${kind}:${c}`] ?? '' } })
                        setAdding({ ...adding, [kind]: '' })
                      }}>
                        <option value="">{t('Escolha…')}</option>
                        {BUDGET_CATS.filter(([c]) => !rows.some((r) => r.category === c)).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                      </Select>
                    </Field>
                  </div>
                )}
              </div>
            )
          })}
          {draft ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('Aprovada em (assembleia, opcional)')} htmlFor="bd-approved">
                <Select id="bd-approved" value={draft.approved} onChange={(e) => setDraft({ ...draft, approved: e.target.value })}>
                  <option value="">{t('Ainda não aprovada')}</option>
                  {(asms.data?.assemblies ?? []).map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </Select>
              </Field>
              <Field label={t('Observações (vão para o PDF)')} htmlFor="bd-note"><Textarea id="bd-note" rows={3} maxLength={2000} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></Field>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3 text-sm text-tinta-2">
              <span>{d.approvedAssemblyTitle ? t('Aprovada em: {title}', { title: d.approvedAssemblyTitle }) : d.exists ? t('Ainda não aprovada em assembleia.') : manage ? t('Monte a previsão a partir do realizado e leve-a à AGO.') : t('O síndico ainda não montou a previsão deste ano.')}</span>
              {d.documentId && <Link to="/c/$slug/acervo/$docId" params={{ slug, docId: d.documentId }} className="inline-flex items-center gap-1 font-semibold text-caneta"><FileText className="size-4" />{' '}{t('PDF no acervo')}</Link>}
              {d.note && <span className="basis-full">{d.note}</span>}
            </div>
          )}
        </>
      )}
      {season && draft && (
        <WeightsDialog
          label={season.label}
          weights={season.weights}
          fetchLastYear={async () => {
            const r = await get<{ lines: { kind: Kind; category: string; weights?: number[] | null }[] }>(url('/suggestion'))
            const [kind, category] = season.key.split(':')
            return r.lines.find((l) => l.kind === kind && l.category === category)?.weights ?? null
          }}
          onApply={(w) => { setDraft({ ...draft, weights: { ...draft.weights, [season.key]: w } }); setSeason(null) }}
          onClose={() => setSeason(null)}
        />
      )}
    </section>
  )
}

/** Sazonalidade (ADR 0033): twelve shares for one line; the numbers need not add up, the server normalises them. */
function WeightsDialog({ label: cat, weights, fetchLastYear, onApply, onClose }: { label: string; weights: number[] | null; fetchLastYear: () => Promise<number[] | null>; onApply: (w: number[] | null) => void; onClose: () => void }) {
  const pct = (w: number[]) => w.map((x) => String(Math.round((x / (w.reduce((a, b) => a + b, 0) || 1)) * 1000) / 10))
  const [vals, setVals] = useState<string[]>(pct(weights ?? Array.from({ length: 12 }, () => 1)))
  const [busy, setBusy] = useState(false)
  const nums = vals.map((v) => Math.max(0, toNumber(v)))
  const sum = nums.reduce((a, b) => a + b, 0)
  return (
    <Dialog open onClose={onClose} title={t('Sazonalidade: {category}', { category: cat })}>
      <p className="text-sm text-tinta-2">{t('Quanto do previsto cai em cada mês. Os números não precisam somar 100: o aplicativo ajusta a proporção. Sem isso, a comparação é por doze avos iguais.')}</p>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
        {MONTHS_SHORT.map((m, i) => (
          <Field key={m} label={m} htmlFor={`sz-m-${i}`}><Input id={`sz-m-${i}`} inputMode="decimal" value={vals[i]} onChange={(e) => setVals(vals.map((v, j) => (j === i ? e.target.value : v)))} /></Field>
        ))}
      </div>
      <p className="mt-2 text-sm text-tinta-2">{sum > 0 ? t('Com esses números, {weights}.', { weights: describeWeights(nums) }) : t('Informe ao menos um mês.')}</p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="quiet" busy={busy} onClick={async () => { setBusy(true); try { const w = await fetchLastYear(); if (!w) toast.error(t('Sem lançamentos no ano anterior para esta categoria.')); else setVals(pct(w)) } finally { setBusy(false) } }}>{t('Como no ano anterior')}</Button>
        <Button type="button" variant="quiet" onClick={() => onApply(null)}>{t('Uniforme')}</Button>
        <Button type="button" onClick={() => onApply(sum > 0 ? nums : null)}>{t('Aplicar')}</Button>
      </div>
    </Dialog>
  )
}
